import cx from 'classnames'
import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import Styles from './AddRowInline.module.scss'

import { ArticleFlatData } from 'api/client'
import { CreateFolderProps, FolderClient, isTableOrBoard } from 'api/clients/FolderClient'
import { MentionClient } from 'api/clients/MentionClient'
import { AssigneeType } from 'api/clients/PropertyClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Button } from 'components/Button'
import { CreateInlineMenuWithInput } from 'components/CreateInlineMenu/CreateInlineMenuWithInput'
import { isFileInFolder } from 'components/CreateInlineMenu/utils'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { DuplicateWarningModal } from 'components/DuplicateWarningModal/DuplicateWarningModal'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useModalStore } from 'hooks/stores/useModalStore'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useEvent } from 'react-use'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { useColumnWidthsStore } from 'screens/Finder/components/views/TableView/stores/useColumnWidthsStore'
import { useRootStore } from 'stores/RootStore'
import { AlmanacFile as File, ViewType } from 'types'
import { createNewArticle } from 'utils'
import { reportError } from 'utils/error'
import { MaybeTransitionEffectPayload } from 'utils/workflows/runAfterTransitionEffects'
import useNewEntrypointModal from 'hooks/useNewEntrypointModal'
import { Plus } from 'blaze-ui/icons'

type FolderData = {
  type: AssigneeType.Project
  data: { folder: File }
}

type ArticleData = {
  type: AssigneeType.Article
  data: ArticleFlatData
}

export type ResponseData = FolderData | ArticleData

interface Props {
  viewType: ViewType
  emptyState?: boolean
  onSuccess?: (data: ResponseData, payload?: MaybeTransitionEffectPayload) => Promise<void>
  onCreateFile?: (type: AssigneeType, title: string) => Promise<MaybeTransitionEffectPayload>
  onUploadMediaFile?: () => void
  forceOpen?: boolean
}

type selectedOptionState = {
  activeOption: 'doc' | 'folder'
  activeOptionKeyboard: 'doc' | 'folder'
}
type selectedOptionAction =
  | { type: 'keyboard'; e: React.KeyboardEvent<HTMLInputElement> }
  | { type: 'mouse'; target: 'doc' | 'folder' }
  | { type: 'mouseout' }
  | { type: 'reset' }

export const AddFileInline = ({
  viewType,
  emptyState,
  onSuccess,
  onCreateFile,
  onUploadMediaFile,
  forceOpen,
}: Props) => {
  const auth = useAuth()
  const { openModal, closeModal } = useModalStore()
  const workspace = useCurrentWorkspace()
  const { refresh: refreshFinder, quickAddFile } = useFinderContext()
  const [isProcessing, setIsProcessing] = useState(false)
  const [activeInput, setActiveInput] = useState(false)
  const [inputValue, setInputValue] = useState('Untitled')
  const [inputHasBeenModified, setInputHasBeenModified] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useResponsiveDesign()
  const { currentFolder } = useCurrentFolderStore()
  const handbooksStore = useRootStore().handbooks
  const parentKey = currentFolder?.id
  const parentId = currentFolder?.project_id
  const { firstColWidth, fullTableWidth } = useColumnWidthsStore()
  const inputWidth = emptyState ? '100%' : Math.max(firstColWidth - 40, 356)
  const { openNewEntrypointModal } = useNewEntrypointModal()
  const useCreateModalOnly = true

  useEffect(() => {
    if (activeInput) return

    dispatchSelectedOptionAction({ type: 'reset' })
  }, [activeInput])

  const reset = useCallback(() => {
    setActiveInput(false)
    setIsProcessing(false)
    setInputHasBeenModified(false)
    setInputValue('Untitled')
  }, [])

  const createDoc = useCallback(
    async (createAnother: boolean, docTitle: string) => {
      let modifiedDocTitle = docTitle.trim()
      if (modifiedDocTitle.length === 0) {
        modifiedDocTitle = 'Untitled'
      }

      const payload: MaybeTransitionEffectPayload = await (onCreateFile?.(AssigneeType.Article, modifiedDocTitle) ||
        Promise.resolve(undefined))

      if (payload === null) return

      const hideLoading = showToastMessage('Creating document...', 'loading', undefined, 0)
      setIsProcessing(true)
      const response = await createNewArticle({
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        workspaceKey: auth!.company_key,
        projectId: parentId,
        title: modifiedDocTitle,
        skipHeading: true,
        createArticleViewEvent: true,
      })

      quickAddFile({
        ...response.data.article,
        type: 'article',
        accessor_info: '',
        owner: '',
        accessor_names: [],
        ancestor_projects: [],
        updated_at: '',
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        workspace_id: workspace?.key!,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        real_workspace_id: workspace?.id!,
      })

      hideLoading()
      reset()

      if (response.type === SUCCESS_RESPONSE_TYPE) {
        if (onSuccess) await onSuccess({ type: AssigneeType.Article, data: response.data }, payload)
        refreshFinder()
        handbooksStore.refresh(currentFolder?.handbook?.key)
        if (createAnother) setActiveInput(true)
      } else {
        reportError(new Error('Error while creating a new document'), { metaData: { response } })
        showErrorMessageToast('Something went wrong 😢')
      }
    },
    [
      auth,
      onCreateFile,
      onSuccess,
      parentId,
      quickAddFile,
      refreshFinder,
      handbooksStore,
      workspace?.id,
      workspace?.key,
      currentFolder?.handbook?.key,
      reset,
    ],
  )

  const createSuggestedDoc = async (id: string, title: string, close: () => void) => {
    if (!currentFolder) return
    const isAlreadyInFolder = await isFileInFolder(id, currentFolder.id)

    if (isAlreadyInFolder) {
      close()
      openModal(DuplicateWarningModal, {
        customModal: true,
        modalProps: {
          title: title,
          onClose: closeModal,
          folderName: currentFolder.title,
        },
      })
      return
    }

    const hideLoading = showToastMessage('Creating mentioned document...', 'loading', undefined, 0)
    setIsProcessing(true)
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const response = await MentionClient.createMentionedArticle(id, currentFolder.id!)
    close()

    quickAddFile({
      ...response.data.article,
      type: 'article',
      accessor_info: '',
      owner: '',
      accessor_names: [],
      ancestor_projects: [],
      updated_at: '',
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      workspace_id: workspace?.key!,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      real_workspace_id: workspace?.id!,
    })

    hideLoading()
    reset()

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      refreshFinder()
      handbooksStore.refresh(currentFolder?.handbook?.key)
    } else {
      reportError(new Error('Error while creating a new mentioned document'), { metaData: { response } })
      showErrorMessageToast('Something went wrong 😢')
    }
  }

  const createFolder = useCallback(
    async (
      createAnother: boolean,
      folderTitle: string,
      chosenOption: NonNullable<CreateFolderProps['defaultView']>,
    ) => {
      let modifiedFolderTitle = folderTitle.trim()
      if (modifiedFolderTitle.length === 0) {
        modifiedFolderTitle = 'Untitled'
      }
      const payload = await (onCreateFile?.(AssigneeType.Project, modifiedFolderTitle) || Promise.resolve(undefined))

      if (payload === null) return

      const defaultView = isTableOrBoard(chosenOption) ? chosenOption : 'table_view'

      const hideLoading = showToastMessage('Creating a folder...', 'loading', undefined, 0)
      setIsProcessing(true)
      const response = await FolderClient.createFolder({
        title: modifiedFolderTitle,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        workspaceKey: workspace?.key!,
        parentKey,
        createFolderViewEvent: true,
        defaultView,
        handbookMode: false,
      })

      quickAddFile({
        ...response.data.folder,
        type: 'folder',
        accessor_info: '',
        owner: '',
        accessor_names: [],
        ancestor_projects: [],
        updated_at: '',
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        workspace_id: workspace?.key!,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        real_workspace_id: workspace?.id!,
      })

      hideLoading()
      reset()

      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        if (onSuccess) await onSuccess({ type: AssigneeType.Project, data: response.data }, payload)
        refreshFinder()
        handbooksStore.refresh(currentFolder?.handbook?.key)
        if (createAnother) setActiveInput(true)
      } else {
        const returnedError = response.data.errors[0]

        if (returnedError?.detail === 'has already been taken') {
          showErrorMessageToast(`Folder named "${modifiedFolderTitle}" already exists`)
        } else {
          showErrorMessageToast('Something went wrong 😢')
        }
        reportError(new Error('Error while creating a new folder'), { metaData: { response } })
      }
    },
    [
      onCreateFile,
      workspace?.key,
      workspace?.id,
      parentKey,
      quickAddFile,
      onSuccess,
      refreshFinder,
      handbooksStore,
      currentFolder?.handbook?.key,
      reset,
    ],
  )

  const selectedOptionReducer = useCallback(
    (prevState: selectedOptionState, action: selectedOptionAction): selectedOptionState => {
      switch (action.type) {
        case 'keyboard': {
          const { e } = action
          switch (e.key) {
            case 'ArrowDown':
            case 'Tab':
              e.preventDefault()
              return { ...prevState, activeOptionKeyboard: 'folder', activeOption: 'folder' }
            case 'ArrowUp':
              e.preventDefault()
              return { ...prevState, activeOptionKeyboard: 'doc', activeOption: 'doc' }
            case 'Enter':
              const createAnother = e.shiftKey
              if (prevState.activeOption === 'doc') {
                createDoc(createAnother, inputValue)
              } else if (prevState.activeOption === 'folder') {
                createFolder(createAnother, inputValue, 'table_view')
              }
              return { ...prevState, activeOptionKeyboard: 'doc', activeOption: 'doc' }
            case 'Escape':
              reset()
              return { ...prevState, activeOptionKeyboard: 'doc', activeOption: 'doc' }
          }
          return prevState
        }
        case 'mouse': {
          const { target } = action
          switch (target) {
            case 'doc':
              return { ...prevState, activeOption: 'doc' }
            case 'folder':
              return { ...prevState, activeOption: 'folder' }
          }
        }
        // eslint-disable-next-line no-fallthrough
        case 'mouseout': {
          return { ...prevState, activeOption: prevState.activeOptionKeyboard }
        }
        case 'reset': {
          return { activeOption: 'doc', activeOptionKeyboard: 'doc' }
        }
      }
    },
    [createDoc, createFolder, inputValue, reset],
  )
  const [selectedOptionState, dispatchSelectedOptionAction] = useReducer(selectedOptionReducer, {
    activeOption: 'doc',
    activeOptionKeyboard: 'doc',
  })

  const dropdownContentRef = useRef<HTMLDivElement>(null)
  useEvent(
    'mousemove',
    (e) => {
      if (selectedOptionState.activeOption === selectedOptionState.activeOptionKeyboard) return
      if (e.target !== dropdownContentRef.current && !dropdownContentRef.current?.contains(e.target)) {
        dispatchSelectedOptionAction({ type: 'mouseout' })
      }
    },
    undefined,
  )

  useEffect(() => {
    if (!inputHasBeenModified && inputValue !== 'Untitled') setInputHasBeenModified(true)
  }, [inputValue, inputHasBeenModified])

  useEffect(() => {
    if (activeInput && inputRef?.current) {
      inputRef.current.select()
    }
  }, [activeInput])

  const viewStyles = {
    [ViewType.List]: {
      containerWidthStyle: undefined,
      inputWidthStyle: undefined,
    },
    [ViewType.Table]: {
      containerWidthStyle: { width: fullTableWidth },
      inputWidthStyle: { width: inputWidth },
    },
    [ViewType.Board]: {
      containerWidthStyle: undefined,
      inputWidthStyle: !!containerRef.current?.offsetWidth ? { width: containerRef.current?.offsetWidth } : undefined,
    },
    [ViewType.Gallery]: {
      containerWidthStyle: undefined,
      inputWidthStyle: undefined,
    },
    [ViewType.Folder]: {
      containerWidthStyle: undefined,
      inputWidthStyle: undefined,
    },
    [ViewType.Calendar]: {
      containerWidthStyle: undefined,
      inputWidthStyle: undefined,
    },
  }

  const { containerWidthStyle } = viewStyles[viewType]

  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setActiveInput(true)
    } else {
      reset()
    }
  }

  const overrideWidth = () => {
    if (isMobile) return undefined
    if (viewType === ViewType.Board) return containerRef.current?.offsetWidth
    if (viewType === ViewType.Table) return firstColWidth

    return undefined
  }

  // Using a variable so as to not delete all the dropdown logic below in case we want to use it again
  if (useCreateModalOnly)
    return (
      <div className={Styles.buttonContainer}>
        <Button
          id="add-file-inline-button"
          className={cx(Styles.newButton, Styles[viewType], {
            [Styles.hidden]: activeInput,
          })}
          frontIcon={Plus}
          onPress={() => openNewEntrypointModal({ createInFolder: currentFolder })}
          variant="tertiary"
        >
          Create New
        </Button>
      </div>
    )

  return (
    <div
      className={cx(Styles.container, Styles[viewType], {
        [Styles.active]: activeInput,
      })}
      ref={containerRef}
      style={containerWidthStyle}
    >
      <DropdownTrigger
        placement="bottom-start"
        onOpenChange={onOpenChange}
        containFocus={isMobile}
        useSqueeze={false}
        popoverClassName={cx(Styles.popoverOverride, Styles[viewType], {
          [Styles.popoverOverrideList]: viewType === ViewType.List,
          [Styles.popoverOverrideTable]: viewType === ViewType.Table,
        })}
        forceOpen={forceOpen}
      >
        <div className={Styles.buttonContainer}>
          <Button
            id="add-file-inline-button"
            className={cx(Styles.newButton, Styles[viewType], {
              [Styles.hidden]: activeInput,
            })}
            frontIcon={Plus}
            variant="tertiary"
          >
            Create New
          </Button>
        </div>
        {(close, _isOpen, isFlipped) => (
          <CreateInlineMenuWithInput
            showSuggestions={viewType !== ViewType.Board}
            close={close}
            newItemText={viewType === ViewType.Board}
            onCreateDoc={(createAnother: boolean, title: string) => {
              createDoc(createAnother, title)
              if (!createAnother) close()
            }}
            onUploadMediaFile={onUploadMediaFile}
            onCreateSuggestedDoc={(id: string, title: string) => createSuggestedDoc(id, title, close)}
            onCreateFolder={(
              createAnother: boolean,
              title: string,
              chosenOption: NonNullable<CreateFolderProps['defaultView']>,
            ) => {
              createFolder(createAnother, title, chosenOption)
              if (!createAnother) close()
            }}
            dropdownStyle={cx(Styles.fullWidth, {
              [Styles.mobileDropdown]: isMobile,
            })}
            isFlipped={!!isFlipped}
            overrideWidth={overrideWidth()}
            isProcessing={isProcessing}
            inputWrapperClassName={cx(Styles.createInlineMenu, {
              [Styles.flipped]: isFlipped,
            })}
          />
        )}
      </DropdownTrigger>
    </div>
  )
}
